import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { OtpOutcome, VerifyOtpOutcome } from "./types";
import { LanguageToggle } from "./LanguageToggle";
import { OtpCodeInput } from "./OtpCodeInput";

export interface OtpStepProps {
  phoneNumber: string;
  onVerify: (code: string) => Promise<VerifyOtpOutcome>;
  onResend: () => Promise<OtpOutcome>;
  onBack: () => void;
  isVerifying: boolean;
}

const OTP_CODE_LENGTH = 6;

const OTP_EXPIRED = "OT_002";
const OTP_MAX_ATTEMPTS_EXCEEDED = "OT_003";
const OTP_INVALID_CODE = "OT_004";
const OTP_MAX_SEND_COUNT_EXCEEDED = "OT_005";

const MAX_OTP_ATTEMPTS = 3;

const OTP_RESEND_COOLDOWN_SECONDS = 60;

type OtpMessage =
  | { type: "expired" }
  | { type: "wrongCode"; attemptsRemaining: number }
  | { type: "exhausted" }
  | { type: "tooManySends" }
  | { type: "generic" }
  | null;

export const OtpStep: React.FC<OtpStepProps> = ({
  phoneNumber,
  onVerify,
  onResend,
  onBack,
  isVerifying,
}) => {
  const { t } = useTranslation("sharedLogin");
  const [code, setCode] = useState("");
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [message, setMessage] = useState<OtpMessage>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(
    OTP_RESEND_COOLDOWN_SECONDS,
  );
  const [isResending, setIsResending] = useState(false);
  const [resendKey, setResendKey] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setResendSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendKey]);

  const isExhausted = message?.type === "exhausted";
  const isValidLength = code.length === OTP_CODE_LENGTH;
  const canResend = resendSecondsLeft <= 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isVerifying || isExhausted || !isValidLength) return;
    const result = await onVerify(code);
    if (result.success) return;
    setCode("");
    if (result.errorCode === OTP_EXPIRED) {
      setMessage({ type: "expired" });
      return;
    }
    if (result.errorCode === OTP_MAX_ATTEMPTS_EXCEEDED) {
      setMessage({ type: "exhausted" });
      return;
    }
    if (result.errorCode === OTP_INVALID_CODE) {
      setAttemptsUsed((prev) => {
        const next = prev + 1;
        setMessage({
          type: "wrongCode",
          attemptsRemaining: Math.max(MAX_OTP_ATTEMPTS - next, 0),
        });
        return next;
      });
      return;
    }
    setMessage({ type: "generic" });
  };

  const handleResend = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    try {
      const result = await onResend();
      if (result.success) {
        setCode("");
        setAttemptsUsed(0);
        setMessage(null);
        setResendSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
        setResendKey((prev) => prev + 1);
        return;
      }
      setMessage(
        result.errorCode === OTP_MAX_SEND_COUNT_EXCEEDED
          ? { type: "tooManySends" }
          : { type: "generic" },
      );
    } finally {
      setIsResending(false);
    }
  };

  const messageText =
    message === null
      ? null
      : message.type === "expired"
        ? t("otp.errExpired")
        : message.type === "wrongCode"
          ? t("otp.errWrongCode", { count: message.attemptsRemaining })
          : message.type === "exhausted"
            ? t("otp.errTooManyAttempts")
            : message.type === "tooManySends"
              ? t("otp.errTooManySends")
              : t("otp.errGeneric");

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="self-start font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60"
      >
        {t("otp.back")}
      </button>

      <div className="flex flex-col gap-4 md:gap-6 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8">
        <LanguageToggle />

        <h1 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
          {t("otp.heading")}
        </h1>
        <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
          {t("otp.subtitle", { phone: phoneNumber })}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <OtpCodeInput
            length={OTP_CODE_LENGTH}
            value={code}
            onChange={setCode}
          />

          {messageText && (
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
              {messageText}
            </p>
          )}

          <button
            type="submit"
            disabled={isVerifying || isExhausted}
            className={`flex h-14 w-full items-center justify-center rounded-2xl font-lexend text-[16px]/[20px] font-bold tracking-normal text-white ${
              isVerifying || isExhausted
                ? "cursor-not-allowed bg-gray-300"
                : "bg-primary-100"
            }`}
          >
            {isVerifying ? t("otp.verifying") : t("otp.submit")}
          </button>

          <button
            type="button"
            disabled={!canResend || isResending || isVerifying}
            onClick={handleResend}
            className={`mx-auto text-center font-lexend text-[12px]/[16px] font-normal tracking-normal md:text-[14px]/[18px] ${
              canResend ? "text-ink-60" : "text-ink-40 cursor-not-allowed"
            }`}
          >
            {canResend
              ? t("otp.resend")
              : t("otp.resendCountdown", { seconds: resendSecondsLeft })}
          </button>
        </form>
      </div>
    </>
  );
};
